/*!
 * Sidebar Component
 * Hierarchical navigation with collapsible groups
 */

<let/openGroups=new Set((input.initialOpenGroups || []))/>
<let/currentPath=(typeof window !== "undefined" && window.location && window.location.pathname) || ""/>

<style/styles>
  .content {
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }

  .sectionHeader {
    padding: var(--sidebar-category-padding, var(--space-sm) 0);
    font-size: var(--sidebar-category-font-size, 0.75rem);
    font-weight: var(--sidebar-category-font-weight, 600);
    text-transform: var(--sidebar-category-text-transform, uppercase);
    color: var(--text-2, #6b7280);
    letter-spacing: 0.05em;
  }

  .nav {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--sidebar-item-gap, var(--space-xs));
  }

  .item {
    list-style: none;
  }

  .link {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--sidebar-item-padding, var(--space-sm) var(--space-md));
    font-size: var(--sidebar-item-font-size, 0.875rem);
    font-weight: var(--sidebar-item-font-weight, 400);
    color: var(--text-2, #6b7280);
    text-decoration: none;
    border-radius: var(--sidebar-item-border-radius, 4px);
    transition: all var(--transition-fast, 0.15s);
    line-height: var(--sidebar-item-height, 1.4);
    height: var(--sidebar-item-height, auto);
  }

  .link:hover {
    color: var(--text-1, #1f2937);
    background: var(--sidebar-hover-background, rgba(0, 0, 0, 0.05));
  }

  .link.active {
    color: var(--color-primary-1, #3a5ccc);
    background: var(--sidebar-active-background, rgba(100, 108, 255, 0.14));
    font-weight: var(--sidebar-item-font-weight, 500);
    border-left: var(--sidebar-active-border, 2px solid #3451b2);
  }

  .icon {
    flex-shrink: 0;
    font-size: 1rem;
  }

  .text {
    flex: 1;
  }

  .group {
    display: flex;
    flex-direction: column;
  }

  .groupHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--sidebar-item-padding, var(--space-sm) var(--space-md));
    font-size: var(--sidebar-item-font-size, 0.875rem);
    font-weight: 500;
    color: var(--text-1, #1f2937);
    cursor: pointer;
    border-radius: var(--sidebar-item-border-radius, 4px);
    transition: background-color var(--transition-fast, 0.15s);
    user-select: none;
    height: var(--sidebar-item-height, auto);
  }

  .groupHeader:hover {
    background: var(--sidebar-hover-background, rgba(0, 0, 0, 0.05));
  }

  .groupTitle {
    flex: 1;
  }

  .groupIcon {
    flex-shrink: 0;
    transition: transform var(--transition-fast, 0.15s);
  }

  .groupIcon.rotated {
    transform: rotate(90deg);
  }

  .groupItems {
    list-style: none;
    padding-left: var(--space-lg);
    margin-top: var(--space-xs);
    display: flex;
    flex-direction: column;
    gap: var(--sidebar-item-gap, var(--space-xs));
    border-left: 1px solid var(--border-divider, #e5e7eb);
  }

  .groupItems .link {
    font-size: var(--sidebar-item-font-size, 0.875rem);
    color: var(--text-2, #6b7280);
  }
</style>

<div class=styles.content role="navigation" aria-label="Sidebar navigation">
  <!-- Sidebar Sections -->
  <for|section| of=(input.sections || [])>
    <div class=styles.section>
      <!-- Section Header -->
      <if=section.text>
        <div class=styles.sectionHeader>${section.text}</div>
      </if>

      <!-- Section Items -->
      <ul class=styles.nav>
        <for|item| of=section.items>
          <if=item.items && item.items.length>
            <!-- Item with children (collapsible group) -->
            <li class=styles.group>
              <div
                class=styles.groupHeader
                onClick(e) {
                  if (openGroups.has(item.text)) {
                    openGroups.delete(item.text);
                  } else {
                    openGroups.add(item.text);
                  }
                }
                role="button"
                tabindex="0"
                aria-expanded=openGroups.has(item.text)>
                <span class=styles.groupTitle>${item.text}</span>
                <let/rotatedClass=openGroups.has(item.text) ? styles.groupIcon + ' ' + styles.rotated : styles.groupIcon/>
                <svg
                  class=rotatedClass
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>

              <if=openGroups.has(item.text)>
                <ul class=styles.groupItems>
                  <for|subitem| of=item.items>
                    <let/subitemClass=currentPath === subitem.link ? styles.link + ' ' + styles.active : styles.link/>
                    <li class=styles.item>
                      <a
                        class=subitemClass
                        href=subitem.link>
                        <if=subitem.icon>
                          <span class=styles.icon>${subitem.icon}</span>
                        </if>
                        <span class=styles.text>${subitem.text}</span>
                      </a>
                    </li>
                  </for>
                </ul>
              </if>
            </li>
          </if>

          <if=!item.items || item.items.length === 0>
            <!-- Regular item (no children) -->
            <let/itemClass=currentPath === item.link ? styles.link + ' ' + styles.active : styles.link/>
            <li class=styles.item>
              <a
                class=itemClass
                href=item.link>
                <if=item.icon>
                  <span class=styles.icon>${item.icon}</span>
                </if>
                <span class=styles.text>${item.text}</span>
              </a>
            </li>
          </if>
        </for>
      </ul>
    </div>
  </for>
</div>
